import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { caseService, customerService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Eye, Loader2 } from "lucide-react";
import { STATUS_COLOR, PRIORITY_COLOR } from "./constants";

const CASE_STATUS = ["Open", "In Progress", "Resolved", "Closed"];
const EMPTY = { title: "", description: "", priority: "Medium", customerId: "", tenantId: "" };

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function CasesPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await caseService.list({})).items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const loadCustomers = (tid) => customerService.list({ tenantId: tid || undefined, size: 200 }).then((d) => setCustomers(d.items || [])).catch(() => {});
  const openCreate = () => { setForm(EMPTY); setOpen(true); if (!isSuper) loadCustomers(); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (isSuper && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!isSuper) delete payload.tenantId;
      if (!payload.customerId) delete payload.customerId;
      await caseService.create(payload);
      toast.success("Case created"); setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete case ${r.caseNumber}?`)) return;
    try { await caseService.remove(r.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const viewCase = async (r) => {
    try { setDetail(await caseService.get(r.id)); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("CASE.CREATE");
  const canDelete = hasPermission("CASE.DELETE");

  const columns = [
    { key: "caseNumber", header: "Case", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.caseNumber}</span> },
    { key: "title", header: "Title", render: (r) => <span className="font-medium text-foreground">{r.title}</span> },
    { key: "priority", header: "Priority", render: (r) => <Badge color={PRIORITY_COLOR[r.priority] || "slate"}>{r.priority}</Badge> },
    { key: "status", header: "Status", render: (r) => <Badge color={STATUS_COLOR[r.status] || "slate"}>{r.status}</Badge> },
    { key: "ticketIds", header: "Tickets", render: (r) => <span className="text-muted-foreground">{(r.ticketIds || []).length}</span> },
    {
      key: "_a", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => viewCase(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`case-view-${r.id}`}><Eye className="h-4 w-4" /></button>
          {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`case-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="cases-page">
      <PageHeader title="Cases" subtitle="Group related tickets under a single case"
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="case-create-btn">
            <Plus className="h-4 w-4" /> New Case
          </button>
        )}
      />
      <DataTable columns={columns} rows={rows} loading={loading} testid="cases-table" empty="No cases yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="case-dialog">
            <h3 className="text-lg font-bold mb-4">New Case</h3>
            <div className="space-y-3.5">
              {isSuper && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => { setForm({ ...form, tenantId: e.target.value, customerId: "" }); loadCustomers(e.target.value); }} className="ciq-input" data-testid="case-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Title *"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="ciq-input" data-testid="case-title" /></Field>
              <Field label="Customer (optional)">
                <select value={form.customerId} onChange={(e) => setForm({ ...form, customerId: e.target.value })} className="ciq-input" data-testid="case-customer">
                  <option value="">—</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.customerName}</option>)}
                </select>
              </Field>
              <Field label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ciq-input" rows={2} /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="case-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="case-detail">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-bold">{detail.title}</h3>
              <Badge color={STATUS_COLOR[detail.status] || "slate"}>{detail.status}</Badge>
            </div>
            <p className="text-xs font-mono text-muted-foreground mb-4">{detail.caseNumber}</p>
            <p className="text-sm text-muted-foreground mb-4">{detail.description || "No description"}</p>
            <h4 className="text-sm font-semibold mb-2">Linked tickets ({(detail.tickets || []).length})</h4>
            <div className="space-y-2">
              {(detail.tickets || []).length === 0 && <p className="text-sm text-muted-foreground">No tickets linked yet</p>}
              {(detail.tickets || []).map((t) => (
                <div key={t.id} className="flex items-center justify-between text-sm border-b border-border pb-2">
                  <span><span className="font-mono text-xs text-muted-foreground mr-2">{t.ticketNumber}</span>{t.subject}</span>
                  <Badge color={STATUS_COLOR[t.status] || "slate"}>{t.status}</Badge>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-6"><button onClick={() => setDetail(null)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
